import assistantWorkspaceOauthClient from "../../../config/google-cloud/ystnj-assistant-workspace-oauth-client.json";

type AssistantWorkspaceGoogleOauthManifest =
  typeof assistantWorkspaceOauthClient;

export type AssistantWorkspaceGoogleOauthScope =
  AssistantWorkspaceGoogleOauthManifest["scopes"][number];

export const ASSISTANT_WORKSPACE_GOOGLE_OAUTH_CLIENT_DISPLAY_NAME =
  assistantWorkspaceOauthClient.client.displayName;

export const ASSISTANT_WORKSPACE_GOOGLE_OAUTH_PROJECT_ID =
  assistantWorkspaceOauthClient.client.projectId;

export const ASSISTANT_WORKSPACE_GOOGLE_OAUTH_CALLBACK_PATH =
  assistantWorkspaceOauthClient.bridge.callbackPath;

export const ASSISTANT_WORKSPACE_GOOGLE_OAUTH_CALLBACK_ID =
  assistantWorkspaceOauthClient.bridge.callbackQuery.bridgeCallback;

export const ASSISTANT_WORKSPACE_GOOGLE_OAUTH_REDIRECT_URIS =
  assistantWorkspaceOauthClient.bridge.redirectUris as readonly string[];

export const ASSISTANT_WORKSPACE_GOOGLE_OAUTH_SCOPES =
  assistantWorkspaceOauthClient.scopes as readonly AssistantWorkspaceGoogleOauthScope[];

export const ASSISTANT_WORKSPACE_GOOGLE_OAUTH_SECRET_NAMES =
  assistantWorkspaceOauthClient.awsSecretNames;

const ALLOWED_REDIRECT_URI_SET = new Set(
  ASSISTANT_WORKSPACE_GOOGLE_OAUTH_REDIRECT_URIS,
);

const ALLOWED_SCOPE_SET = new Set<string>(
  ASSISTANT_WORKSPACE_GOOGLE_OAUTH_SCOPES,
);

const normalizeUrl = (value: string | null | undefined): string | null => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return null;
  }
  try {
    return new URL(trimmed).toString();
  } catch {
    return null;
  }
};

export const buildAssistantWorkspaceGoogleRedirectUri = (
  origin: string | null | undefined,
): string | null => {
  const normalizedOrigin = normalizeUrl(origin);
  if (!normalizedOrigin) {
    return null;
  }

  const callbackUrl = new URL(
    ASSISTANT_WORKSPACE_GOOGLE_OAUTH_CALLBACK_PATH,
    normalizedOrigin,
  );
  callbackUrl.searchParams.set(
    "bridgeCallback",
    ASSISTANT_WORKSPACE_GOOGLE_OAUTH_CALLBACK_ID,
  );
  return callbackUrl.toString();
};

export const isAllowedAssistantWorkspaceGoogleRedirectUri = (
  redirectUri: string | null | undefined,
): boolean => {
  const normalized = normalizeUrl(redirectUri);
  return normalized ? ALLOWED_REDIRECT_URI_SET.has(normalized) : false;
};

export const isAllowedAssistantWorkspaceGoogleOrigin = (
  origin: string | null | undefined,
): boolean =>
  isAllowedAssistantWorkspaceGoogleRedirectUri(
    buildAssistantWorkspaceGoogleRedirectUri(origin),
  );

export const normalizeAssistantWorkspaceGoogleRequestedScopes = (
  requestedScopes: readonly string[] | null | undefined,
): AssistantWorkspaceGoogleOauthScope[] => {
  if (!requestedScopes?.length) {
    return [...ASSISTANT_WORKSPACE_GOOGLE_OAUTH_SCOPES];
  }

  const requested = new Set(
    requestedScopes
      .map((scope) => String(scope ?? "").trim())
      .filter((scope) => ALLOWED_SCOPE_SET.has(scope)),
  );

  if (!requested.size) {
    return [...ASSISTANT_WORKSPACE_GOOGLE_OAUTH_SCOPES];
  }

  return ASSISTANT_WORKSPACE_GOOGLE_OAUTH_SCOPES.filter((scope) =>
    requested.has(scope),
  );
};
